import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Mail, Lock, User, Phone, AtSign, Eye, EyeOff, Check, X as XIcon, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

export const AuthModal = ({ isOpen, onClose, defaultTab = 'login' }) => {
    const navigate = useNavigate();
    const { login, register, checkAvailability } = useAuth();
    
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [activeTab, setActiveTab] = useState(defaultTab);
    
    // Login form
    const [loginData, setLoginData] = useState({ 
        identifier: '', 
        password: '' 
    });
    
    // Register form - simplified
    const [registerType, setRegisterType] = useState('nickname'); // nickname, email, phone
    const [registerData, setRegisterData] = useState({
        nickname: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
    });
    
    // Availability check
    const [availability, setAvailability] = useState({
        nickname: null,
        email: null,
        phone: null
    });
    const [checkingAvailability, setCheckingAvailability] = useState(false);

    // Check availability with debounce
    useEffect(() => {
        const value = registerData[registerType];
        if (!value || value.length < 3) {
            setAvailability(prev => ({ ...prev, [registerType]: null }));
            return;
        }

        const timer = setTimeout(async () => {
            setCheckingAvailability(true);
            try {
                const result = await checkAvailability(
                    registerType === 'nickname' ? value : null,
                    registerType === 'email' ? value : null,
                    registerType === 'phone' ? value : null
                );
                setAvailability(prev => ({ ...prev, [registerType]: result[registerType] }));
            } catch (error) {
                console.error('Check availability error:', error);
            }
            setCheckingAvailability(false);
        }, 500);

        return () => clearTimeout(timer);
    }, [registerData.nickname, registerData.email, registerData.phone, registerType, checkAvailability]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        
        try {
            await login(loginData.identifier, loginData.password);
            toast.success('Zalogowano pomyślnie!');
            onClose();
            navigate('/');
        } catch (error) {
            toast.error(error.message);
        }
        
        setIsLoading(false);
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        
        // Validate password match
        if (registerData.password !== registerData.confirmPassword) {
            toast.error('Hasła nie są takie same');
            return;
        }
        
        // Validate password length
        if (registerData.password.length < 6) {
            toast.error('Hasło musi mieć minimum 6 znaków');
            return;
        }
        
        // Check if identifier is available
        if (availability[registerType] === false) {
            toast.error(`Ten ${registerType === 'nickname' ? 'nick' : registerType === 'email' ? 'email' : 'telefon'} jest już zajęty`);
            return;
        }

        setIsLoading(true);
        
        try {
            const data = {
                password: registerData.password,
                [registerType]: registerData[registerType]
            };
            
            await register(data);
            toast.success('Konto utworzone! Witaj w Klasowisku! 🎉');
            onClose();
            navigate('/profile');
        } catch (error) {
            toast.error(error.message);
        }
        
        setIsLoading(false);
    };

    const getIdentifierPlaceholder = () => {
        switch (registerType) {
            case 'nickname': return 'np. JanekK2000';
            case 'email': return 'twoj@email.pl';
            case 'phone': return '500 123 456';
            default: return '';
        }
    };

    const getIdentifierIcon = () => {
        switch (registerType) {
            case 'nickname': return <AtSign className="w-4 h-4" />;
            case 'email': return <Mail className="w-4 h-4" />;
            case 'phone': return <Phone className="w-4 h-4" />;
            default: return <User className="w-4 h-4" />;
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="nk-box p-0 max-w-md overflow-hidden" data-testid="auth-modal">
                <DialogHeader className="nk-box-header">
                    <DialogTitle className="flex items-center justify-between">
                        <span className="font-display text-lg">
                            <span className="text-accent-green">Klaso</span>
                            <span className="text-accent-yellow">wisko</span>
                            <span className="text-primary-foreground/80">.pl</span>
                        </span>
                    </DialogTitle>
                </DialogHeader>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 bg-muted m-4 mb-0" style={{ width: 'calc(100% - 2rem)' }}>
                        <TabsTrigger value="login" className="font-semibold" data-testid="login-tab">Logowanie</TabsTrigger>
                        <TabsTrigger value="register" className="font-semibold" data-testid="register-tab">Rejestracja</TabsTrigger>
                    </TabsList>

                    {/* Login Form */}
                    <TabsContent value="login" className="p-4 pt-4">
                        <form onSubmit={handleLogin} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="login-identifier" className="text-sm font-medium">
                                    Nick, email lub telefon
                                </Label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        id="login-identifier"
                                        type="text"
                                        placeholder="Twój nick, email lub telefon"
                                        value={loginData.identifier}
                                        onChange={(e) => setLoginData({ ...loginData, identifier: e.target.value })}
                                        className="nk-input pl-10"
                                        required
                                        data-testid="login-identifier-input"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="login-password" className="text-sm font-medium">
                                    Hasło
                                </Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        id="login-password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={loginData.password}
                                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                                        className="nk-input pl-10 pr-10"
                                        required
                                        data-testid="login-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            <div className="flex items-center justify-between text-sm">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" className="rounded border-border" />
                                    <span className="text-muted-foreground">Zapamiętaj mnie</span>
                                </label>
                                <button type="button" className="text-primary hover:underline">
                                    Nie pamiętasz hasła?
                                </button>
                            </div>

                            <Button 
                                type="submit" 
                                className="w-full nk-button" 
                                disabled={isLoading}
                                data-testid="login-submit-btn"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Logowanie...
                                    </>
                                ) : 'Zaloguj się'}
                            </Button>
                        </form>

                        <div className="mt-4 text-center text-sm text-muted-foreground">
                            <p>Logując się, akceptujesz nasz</p>
                            <Link to="/terms" className="text-primary hover:underline">Regulamin</Link>
                            {' '}i{' '}
                            <Link to="/privacy" className="text-primary hover:underline">Politykę prywatności</Link>
                        </div>
                    </TabsContent>

                    {/* Register Form - Simplified */}
                    <TabsContent value="register" className="p-4 pt-4">
                        <form onSubmit={handleRegister} className="space-y-4">
                            {/* Registration type selector */}
                            <div className="space-y-2">
                                <Label className="text-sm font-medium">
                                    Jak chcesz się zarejestrować?
                                </Label>
                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        variant={registerType === 'nickname' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => setRegisterType('nickname')}
                                        className={registerType === 'nickname' ? 'nk-button' : ''}
                                    >
                                        <AtSign className="w-3 h-3 mr-1" />
                                        Nick
                                    </Button>
                                    <Button
                                        type="button"
                                        variant={registerType === 'email' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => setRegisterType('email')}
                                        className={registerType === 'email' ? 'nk-button' : ''}
                                    >
                                        <Mail className="w-3 h-3 mr-1" />
                                        Email
                                    </Button>
                                    <Button
                                        type="button"
                                        variant={registerType === 'phone' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => setRegisterType('phone')}
                                        className={registerType === 'phone' ? 'nk-button' : ''}
                                    >
                                        <Phone className="w-3 h-3 mr-1" />
                                        Telefon
                                    </Button>
                                </div>
                            </div>

                            {/* Dynamic identifier input */}
                            <div className="space-y-2">
                                <Label htmlFor="register-identifier" className="text-sm font-medium">
                                    {registerType === 'nickname' ? 'Twój nick' : registerType === 'email' ? 'Twój email' : 'Twój telefon'}
                                </Label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                        {getIdentifierIcon()}
                                    </span>
                                    <Input
                                        id="register-identifier"
                                        type={registerType === 'email' ? 'email' : 'text'}
                                        placeholder={getIdentifierPlaceholder()}
                                        value={registerData[registerType]}
                                        onChange={(e) => setRegisterData({ ...registerData, [registerType]: e.target.value })}
                                        className="nk-input pl-10 pr-10"
                                        required
                                        data-testid="register-identifier-input"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2">
                                        {checkingAvailability ? (
                                            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                                        ) : availability[registerType] === true ? (
                                            <Check className="w-4 h-4 text-green-500" />
                                        ) : availability[registerType] === false ? (
                                            <XIcon className="w-4 h-4 text-red-500" />
                                        ) : null}
                                    </span>
                                </div>
                                {availability[registerType] === false && (
                                    <p className="text-xs text-red-500">
                                        Ten {registerType === 'nickname' ? 'nick' : registerType === 'email' ? 'email' : 'telefon'} jest już zajęty
                                    </p>
                                )}
                                {availability[registerType] === true && (
                                    <p className="text-xs text-green-500">
                                        ✓ Dostępny!
                                    </p>
                                )}
                            </div>

                            {/* Password fields */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label htmlFor="register-password" className="text-sm font-medium">
                                        Hasło
                                    </Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input
                                            id="register-password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Min. 6 znaków"
                                            value={registerData.password}
                                            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                                            className="nk-input pl-10"
                                            required
                                            minLength={6}
                                            data-testid="register-password-input"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="register-confirm" className="text-sm font-medium">
                                        Powtórz
                                    </Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input
                                            id="register-confirm"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="••••••"
                                            value={registerData.confirmPassword}
                                            onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                                            className="nk-input pl-10"
                                            required
                                            data-testid="register-confirm-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            {registerData.password && registerData.confirmPassword && registerData.password !== registerData.confirmPassword && (
                                <p className="text-xs text-red-500">Hasła nie są takie same</p>
                            )}

                            <Button 
                                type="submit" 
                                className="w-full nk-button-green" 
                                disabled={isLoading || availability[registerType] === false}
                                data-testid="register-submit-btn"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Rejestracja...
                                    </>
                                ) : 'Dołącz do Klasowiska!'}
                            </Button>

                            <p className="text-xs text-center text-muted-foreground">
                                Resztę danych uzupełnisz w profilu 👍
                            </p>
                        </form>

                        <div className="mt-3 text-center text-xs text-muted-foreground">
                            Rejestrując się, akceptujesz{' '}
                            <Link to="/terms" className="text-primary hover:underline">Regulamin</Link>
                        </div>
                    </TabsContent>
                </Tabs>

                {/* Nostalgic footer */}
                <div className="bg-muted p-3 text-center border-t border-border">
                    <p className="text-xs text-muted-foreground">
                        🎓 Dołącz do ponad <span className="font-semibold text-primary">250 000</span> użytkowników!
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
};
